import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Plus, DollarSign, TrendingUp, BarChart3, Truck, X, Edit2, Trash2, Save,
  Calendar, User, Tag, FileText, Search, PieChart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getAuthToken } from '../utils/auth';
import { BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import WarBoardSnapshot from './dashboard/WarBoardSnapshot';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RV_TYPES = [
  'Class A Motorhome', 'Class B Motorhome', 'Class C Motorhome',
  'Travel Trailer', 'Fifth Wheel', 'Toy Hauler', 'Pop-Up Camper',
  'Truck Camper', 'Teardrop Trailer', 'Other'
];

const CHART_COLORS = ['#004E86', '#F2A900', '#0070B8', '#2AAA8A', '#E85D75', '#8B5CF6', '#F97316', '#06B6D4', '#84CC16', '#EC4899'];

const SalesTrackerTab = ({ isDark, leads, prefilledLead }) => {
  const [sales, setSales] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [activeView, setActiveView] = useState('analytics');
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [form, setForm] = useState({
    lead_id: '', buyer_name: '', buyer_email: '', rv_year: '',
    rv_make: '', rv_model: '', rv_type: '', sale_price: '', cost: '',
    sale_date: new Date().toISOString().split('T')[0], notes: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  // Handle prefilled lead from "Convert to Sale" button
  useEffect(() => {
    if (prefilledLead) {
      setForm(f => ({
        ...f,
        lead_id: prefilledLead.id,
        buyer_name: prefilledLead.name || '',
        buyer_email: prefilledLead.email || '',
        rv_type: prefilledLead.rv_type || '',
      }));
      setShowForm(true);
    }
  }, [prefilledLead]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const token = getAuthToken();
      const [salesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/dealer/sales`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/dealer/sales/analytics/summary`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSales(salesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error('Error loading sales:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ lead_id: '', buyer_name: '', buyer_email: '', rv_year: '', rv_make: '', rv_model: '', rv_type: '', sale_price: '', cost: '', sale_date: new Date().toISOString().split('T')[0], notes: '' });
    setEditingSale(null);
  };

  const handleLeadSelect = (leadId) => {
    if (!leadId) { setForm(f => ({ ...f, lead_id: '' })); return; }
    const lead = (leads || []).find(l => l.id === leadId);
    if (lead) {
      setForm(f => ({
        ...f,
        lead_id: leadId,
        buyer_name: lead.name || f.buyer_name,
        buyer_email: lead.email || f.buyer_email,
        rv_type: lead.rv_type || f.rv_type,
      }));
    }
  };

  const handleSubmit = async () => {
    if (!form.buyer_name.trim() || !form.sale_price || isSaving) return;
    setIsSaving(true);
    try {
      const token = getAuthToken();
      const payload = {
        ...form,
        sale_price: parseFloat(form.sale_price),
        cost: form.cost ? parseFloat(form.cost) : null,
        lead_id: form.lead_id || null,
      };

      if (editingSale) {
        await axios.put(`${API}/dealer/sales/${editingSale.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Sale updated');
      } else {
        await axios.post(`${API}/dealer/sales`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Sale recorded!');

        // Live sync: bump War Board currentUnits for immediate UI feedback.
        // Increment when the sale falls in the current calendar month so the
        // accountability board stays in sync any month of the year.
        try {
          const saleDate = payload.sale_date ? new Date(payload.sale_date) : new Date();
          const today = new Date();
          if (saleDate.getFullYear() === today.getFullYear() && saleDate.getMonth() === today.getMonth()) {
            const raw = localStorage.getItem('salesWarBoard');
            const wb = raw ? JSON.parse(raw) : { currentUnits: 0, deals: [], dailyProgress: {}, lastResetDate: '' };
            wb.currentUnits = (wb.currentUnits || 0) + 1;
            localStorage.setItem('salesWarBoard', JSON.stringify(wb));
            window.dispatchEvent(new StorageEvent('storage', { key: 'salesWarBoard' }));
          }
        } catch (e) { /* non-blocking */ }
      }
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save sale');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (sale) => {
    setForm({
      lead_id: sale.lead_id || '',
      buyer_name: sale.buyer_name || '',
      buyer_email: sale.buyer_email || '',
      rv_year: sale.rv_year || '',
      rv_make: sale.rv_make || '',
      rv_model: sale.rv_model || '',
      rv_type: sale.rv_type || '',
      sale_price: sale.sale_price?.toString() || '',
      cost: sale.cost?.toString() || '',
      sale_date: sale.sale_date || '',
      notes: sale.notes || '',
    });
    setEditingSale(sale);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/dealer/sales/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Sale deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const formatCurrency = (val) => {
    if (!val && val !== 0) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
  };

  const formatMonth = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(mo) - 1]} ${y.slice(2)}`;
  };

  const filteredSales = sales.filter(s =>
    !searchTerm ||
    s.buyer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.rv_make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.rv_model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.rv_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return <div className="flex items-center justify-center py-16" data-testid="sales-loading"><Loader2 className="animate-spin text-[#004E86]" size={36} /></div>;
  }

  return (
    <div data-testid="sales-tracker-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-1 p-1 rounded-lg flex-1 mr-4" style={{ background: isDark ? '#1f2937' : '#f3f4f6' }}>
          {[
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'sales', label: 'All Sales', icon: FileText },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveView(id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${
                activeView === id
                  ? isDark ? 'bg-gray-700 text-white' : 'bg-white text-[#004E86] shadow-sm'
                  : isDark ? 'text-gray-400' : 'text-gray-500'
              }`}
              data-testid={`sales-view-${id}`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>
        <Button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-[#004E86] hover:bg-[#003d6b] text-white flex items-center gap-2"
          data-testid="add-sale-btn"
        >
          <Plus size={16} /> Record Sale
        </Button>
      </div>

      {/* War Board Snapshot (live sync with Sales War Board in Resources) */}
      <WarBoardSnapshot isDark={isDark} />

      {/* Summary Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Sales', value: analytics.total_sales, icon: Truck, color: 'text-[#004E86]', format: (v) => v },
            { label: 'Total Revenue', value: analytics.total_revenue, icon: DollarSign, color: 'text-green-600', format: formatCurrency },
            { label: 'Avg Sale Price', value: analytics.avg_sale_price, icon: TrendingUp, color: 'text-[#F2A900]', format: formatCurrency },
            { label: 'Total Profit', value: analytics.total_profit, icon: BarChart3, color: 'text-purple-600', format: formatCurrency },
          ].map(({ label, value, icon: Icon, color, format }) => (
            <div key={label} className={`rounded-lg p-4 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} className={color} />
                <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span>
              </div>
              <p className={`text-2xl font-bold ${color}`} data-testid={`stat-${label.toLowerCase().replace(/ /g, '-')}`}>{format(value)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Sale Form */}
      {showForm && (
        <div className={`rounded-xl p-5 mb-6 border-2 ${isDark ? 'bg-gray-800 border-[#004E86]' : 'bg-white border-[#004E86]'} shadow-sm`} data-testid="sale-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {editingSale ? 'Edit Sale' : 'Record New Sale'}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); resetForm(); }}><X size={16} /></Button>
          </div>

          {/* Link to Lead */}
          <div className="mb-4">
            <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Link to Lead (optional)</label>
            <Select value={form.lead_id || 'none'} onValueChange={(v) => handleLeadSelect(v === 'none' ? '' : v)}>
              <SelectTrigger className={`w-full ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`} data-testid="lead-select">
                <SelectValue placeholder="Select a lead..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No linked lead</SelectItem>
                {(leads || []).map(l => (
                  <SelectItem key={l.id} value={l.id}>{l.name || l.email} — {l.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Buyer Name *</label>
              <Input value={form.buyer_name} onChange={(e) => setForm(f => ({ ...f, buyer_name: e.target.value }))} placeholder="John Smith" className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''} data-testid="buyer-name-input" />
            </div>
            <div>
              <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Buyer Email</label>
              <Input value={form.buyer_email} onChange={(e) => setForm(f => ({ ...f, buyer_email: e.target.value }))} placeholder="john@email.com" className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''} data-testid="buyer-email-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div>
              <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>RV Year</label>
              <Input value={form.rv_year} onChange={(e) => setForm(f => ({ ...f, rv_year: e.target.value }))} placeholder="2024" className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''} data-testid="rv-year-input" />
            </div>
            <div>
              <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>RV Make</label>
              <Input value={form.rv_make} onChange={(e) => setForm(f => ({ ...f, rv_make: e.target.value }))} placeholder="Jayco" className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''} data-testid="rv-make-input" />
            </div>
            <div>
              <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>RV Model</label>
              <Input value={form.rv_model} onChange={(e) => setForm(f => ({ ...f, rv_model: e.target.value }))} placeholder="Jay Flight" className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''} data-testid="rv-model-input" />
            </div>
            <div>
              <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>RV Type</label>
              <Select value={form.rv_type || 'none'} onValueChange={(v) => setForm(f => ({ ...f, rv_type: v === 'none' ? '' : v }))}>
                <SelectTrigger className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''} data-testid="rv-type-select">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select type...</SelectItem>
                  {RV_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div>
              <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Sale Price *</label>
              <div className="relative">
                <DollarSign size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <Input type="number" value={form.sale_price} onChange={(e) => setForm(f => ({ ...f, sale_price: e.target.value }))} placeholder="45000" className={`pl-8 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`} data-testid="sale-price-input" />
              </div>
            </div>
            <div>
              <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Cost (for profit tracking)</label>
              <div className="relative">
                <DollarSign size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <Input type="number" value={form.cost} onChange={(e) => setForm(f => ({ ...f, cost: e.target.value }))} placeholder="35000" className={`pl-8 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`} data-testid="cost-input" />
              </div>
            </div>
            <div>
              <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Sale Date *</label>
              <Input type="date" value={form.sale_date} onChange={(e) => setForm(f => ({ ...f, sale_date: e.target.value }))} className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''} data-testid="sale-date-input" />
            </div>
          </div>

          <div className="mb-4">
            <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes about the sale..." rows={2} className={`w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#004E86] ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200'}`} data-testid="notes-input" />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={!form.buyer_name.trim() || !form.sale_price || isSaving} className="bg-[#004E86] hover:bg-[#003d6b] text-white" data-testid="save-sale-btn">
              {isSaving ? <Loader2 size={16} className="animate-spin mr-1" /> : <Save size={16} className="mr-1" />}
              {editingSale ? 'Update Sale' : 'Record Sale'}
            </Button>
          </div>
        </div>
      )}

      {/* Analytics View */}
      {activeView === 'analytics' && analytics && (
        <div className="space-y-6">
          {analytics.total_sales === 0 ? (
            <div className={`text-center py-16 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`} data-testid="no-sales">
              <Truck size={48} className={`mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-lg font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>No sales recorded yet</p>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Click "Record Sale" to start tracking</p>
            </div>
          ) : (
            <>
              {/* Monthly Revenue & Units Chart */}
              {analytics.monthly_data.length > 0 && (
                <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
                  <h3 className={`font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <BarChart3 size={18} className="text-[#004E86]" /> Monthly Sales Revenue & Units
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.monthly_data.map(d => ({ ...d, month: formatMonth(d.month) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                      <XAxis dataKey="month" tick={{ fill: isDark ? '#9CA3AF' : '#6B7280', fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fill: isDark ? '#9CA3AF' : '#6B7280', fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: isDark ? '#9CA3AF' : '#6B7280', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{ background: isDark ? '#1f2937' : '#fff', border: 'none', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                        labelStyle={{ color: isDark ? '#fff' : '#111' }}
                        formatter={(value, name) => [name === 'revenue' ? formatCurrency(value) : value, name === 'revenue' ? 'Revenue' : 'Units Sold']}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill="#004E86" radius={[4,4,0,0]} />
                      <Bar yAxisId="right" dataKey="units" name="Units Sold" fill="#F2A900" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sales by RV Type */}
                {analytics.by_rv_type.length > 0 && (
                  <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
                    <h3 className={`font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <PieChart size={18} className="text-[#F2A900]" /> Sales by RV Type
                    </h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <RechartsPie>
                        <Pie
                          data={analytics.by_rv_type}
                          dataKey="count"
                          nameKey="type"
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          label={({ type, count }) => `${type} (${count})`}
                          labelLine={false}
                        >
                          {analytics.by_rv_type.map((_, idx) => (
                            <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value, 'Units']} />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Average Sale Price Trend */}
                {analytics.avg_price_trend.length > 0 && (
                  <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
                    <h3 className={`font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <TrendingUp size={18} className="text-green-600" /> Avg Sale Price Trend
                    </h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={analytics.avg_price_trend.map(d => ({ ...d, month: formatMonth(d.month) }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                        <XAxis dataKey="month" tick={{ fill: isDark ? '#9CA3AF' : '#6B7280', fontSize: 12 }} />
                        <YAxis tick={{ fill: isDark ? '#9CA3AF' : '#6B7280', fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip contentStyle={{ background: isDark ? '#1f2937' : '#fff', border: 'none', borderRadius: '8px' }} formatter={(v) => [formatCurrency(v), 'Avg Price']} />
                        <Line type="monotone" dataKey="avg_price" stroke="#2AAA8A" strokeWidth={3} dot={{ fill: '#2AAA8A', r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Profit Margin Trend */}
              {analytics.profit_margins.length > 0 && analytics.profit_margins.some(p => p.profit > 0) && (
                <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
                  <h3 className={`font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <DollarSign size={18} className="text-purple-600" /> Profit Margin Trend
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analytics.profit_margins.map(d => ({ ...d, month: formatMonth(d.month) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                      <XAxis dataKey="month" tick={{ fill: isDark ? '#9CA3AF' : '#6B7280', fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fill: isDark ? '#9CA3AF' : '#6B7280', fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: isDark ? '#9CA3AF' : '#6B7280', fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip contentStyle={{ background: isDark ? '#1f2937' : '#fff', border: 'none', borderRadius: '8px' }} formatter={(v, name) => [name === 'profit' ? formatCurrency(v) : `${v}%`, name === 'profit' ? 'Profit' : 'Margin %']} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="profit" name="Profit" fill="#8B5CF6" radius={[4,4,0,0]} />
                      <Line yAxisId="right" type="monotone" dataKey="margin" name="Margin %" stroke="#F2A900" strokeWidth={3} dot={{ fill: '#F2A900', r: 4 }} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Sales List View */}
      {activeView === 'sales' && (
        <div>
          <div className="mb-4 relative">
            <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search sales..." className={`pl-9 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`} data-testid="sales-search" />
          </div>

          {filteredSales.length === 0 ? (
            <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
              <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>No sales found</p>
            </div>
          ) : (
            <div className={`rounded-xl shadow-sm overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="overflow-x-auto">
                <table className={`min-w-full divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`} data-testid="sales-table">
                  <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      {['Date', 'Buyer', 'RV', 'Sale Price', 'Profit', 'Actions'].map(h => (
                        <th key={h} className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {filteredSales.map((sale) => (
                      <tr key={sale.id} className={isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} data-testid={`sale-row-${sale.id}`}>
                        <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {sale.sale_date ? new Date(sale.sale_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{sale.buyer_name}</p>
                          {sale.buyer_email && <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{sale.buyer_email}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                            {[sale.rv_year, sale.rv_make, sale.rv_model].filter(Boolean).join(' ') || '-'}
                          </p>
                          {sale.rv_type && <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{sale.rv_type}</p>}
                        </td>
                        <td className={`px-4 py-3 text-sm font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                          {formatCurrency(sale.sale_price)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {sale.profit != null ? (
                            <span className={sale.profit >= 0 ? 'text-green-600 font-medium' : 'text-red-500 font-medium'}>
                              {formatCurrency(sale.profit)}
                            </span>
                          ) : (
                            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleEdit(sale)} className="p-1 text-gray-400 hover:text-[#004E86]" data-testid={`edit-sale-${sale.id}`}><Edit2 size={14} /></button>
                            <button onClick={() => handleDelete(sale.id)} className="p-1 text-gray-400 hover:text-red-500" data-testid={`delete-sale-${sale.id}`}><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SalesTrackerTab;
